import React, { useState } from "react";
import "./Login.css";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { loginUser, logout } from "../../redux/slices/authSlice";
import { requestPasswordReset } from "../../services/api";
import LoginForm from "./LoginForm";
import SignupForm from "./SignupForm";
import ForgotPasswordForm from "./ForgotPasswordForm";
import { homeRouteForRole } from "../../constants/roles";

export default function Login() {
  const [view, setView] = useState("login"); // 'login' | 'signup' | 'forgot'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState("");
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState("");

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    const result = await dispatch(loginUser({ email, password }));
    if (!loginUser.fulfilled.match(result)) return;

    // One login for everyone: the account's role decides which portal opens.
    const homeRoute = homeRouteForRole(result.payload?.user?.role);
    if (!homeRoute) {
      dispatch(
        logout({
          error: "This account has no portal access. Please contact admin.",
        }),
      );
      return;
    }

    navigate(homeRoute, { replace: true });
  };

  const handleSignupClick = (e) => {
    e.preventDefault();
    setView("signup");
  };

  const handleSignupSuccess = () => {
    setView("login");
  };

  const handleForgotPasswordClick = (e) => {
    e.preventDefault();
    setForgotPasswordError("");
    setForgotPasswordSuccess("");
    setView("forgot");
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setForgotPasswordLoading(true);
    setForgotPasswordError("");
    setForgotPasswordSuccess("");

    try {
      const response = await requestPasswordReset(email);
      setForgotPasswordSuccess(
        response?.message ||
          "If the account exists, reset instructions have been sent.",
      );
    } catch (error) {
      setForgotPasswordError(
        error.response?.data?.error ||
          "Unable to process request now. Please try again.",
      );
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const getHeroContent = () => {
    switch (view) {
      case "signup":
        return {
          title: "Create Account",
          subtitle:
            "Join our team of promoters and start tracking your success",
        };
      case "login":
        return {
          title: "Welcome Back!",
          subtitle:
            "Ready to hit the road? Log in to track your visits and close more deals.",
        };
      case "forgot":
        return {
          title: "Reset Password",
          subtitle: "Request a secure reset link for your promoter account.",
        };
      default:
        return {
          title: "Promoter Tracker",
          subtitle: "Khind Promoter Tracking and Sales Commission",
        };
    }
  };

  const heroContent = getHeroContent();

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="hero">
          <div className="hero-icon">
            <div className="hero-icon-inner">
              <img
                src="/assets/logo.png"
                alt="Promoter Tracker Logo"
                style={{ width: "96px", height: "96px" }}
              />
            </div>
          </div>
          <h1>{heroContent.title}</h1>
          <p className="subtitle">{heroContent.subtitle}</p>
        </div>

        <div className="content">
          {view === "login" && (
            <LoginForm
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              onSubmit={handleLoginSubmit}
              onSignupClick={handleSignupClick}
              onForgotPasswordClick={handleForgotPasswordClick}
            />
          )}

          {view === "signup" && (
            <SignupForm
              onBack={() => setView("login")}
              onSuccess={handleSignupSuccess}
            />
          )}

          {view === "forgot" && (
            <ForgotPasswordForm
              email={email}
              setEmail={setEmail}
              onSubmit={handleForgotPasswordSubmit}
              onBack={() => setView("login")}
              isLoading={forgotPasswordLoading}
              error={forgotPasswordError}
              successMessage={forgotPasswordSuccess}
            />
          )}

          <div className="footer-note">Version 1.0</div>
        </div>
      </div>
    </div>
  );
}
